import { AHSeparator, Card, ImageProps, useUploadImage } from "arkh";
import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentTitleInput } from "../../../components/ContentLitstItem";
import {
  RichDescriptionInputControllsWithPrepare,
  RichDescriptionInputFiles,
  RichDescriptionInputMedia,
  RichDescriptionInputText,
} from "../../../components/RichDescriptionInput";
import { useOpenFile } from "../../Files/useOpenFile";
import { RoutineEditContentCard_Routine$key } from "./__generated__/RoutineEditContentCard_Routine.graphql";
import { RoutineEditScreenProps } from "./RoutineEditNavigation";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "../State/RoutineEditState";
import { useUploadImageLink } from "@/src/useUploadImageLink";

export function RoutineEditContentCard(
  props: RoutineEditScreenProps<"RoutineEditMain"> & {
    routine: RoutineEditContentCard_Routine$key;
  }
) {
  const routine = useFragment(
    graphql`
      fragment RoutineEditContentCard_Routine on Routine {
        id
        title
        type
        description
        descriptionFiles {
          id
          type
          name
          url
        }
        descriptionImages {
          id
          ...Image_image
          sizes {
            height
            width
            url
          }
        }
      }
    `,
    props.routine
  );

  const openFile = useOpenFile();

  const params = useRoutineEditState();
  const setParams = useRoutineEditStateSetParams();

  const navigate = props.navigation.navigate;

  const files = useMemo(() => {
    const remoteFiles = routine.descriptionFiles.flatMap((f) =>
      params.removeDescriptionFileIds.includes(f.id)
        ? []
        : [
            {
              id: f.id,
              type: f.type,
              name: f.name ?? "",
              url: f.url,
            },
          ]
    );
    return [
      ...remoteFiles,
      ...params.addDescriptionFiles.filter(
        (file) => remoteFiles.findIndex(({ id }) => id === file.id) === -1
      ),
    ];
  }, [
    params.addDescriptionFiles,
    params.removeDescriptionFileIds,
    routine.descriptionFiles,
  ]);

  const media: {
    width: number;
    height: number;
    image: ImageProps;
    onDelete?: () => void;
  }[] = useMemo(
    () => [
      ...routine.descriptionImages
        .filter((i) => i.sizes && i.sizes.length > 0)
        .map((item) => ({
          height: item.sizes[0].height,
          width: item.sizes[0].width,
          mediaType: "image" as const,
          onDelete: () => {
            setParams({
              removeDescriptionMediaIds: [
                ...(params.removeDescriptionMediaIds ?? []),
                item.id,
              ],
            });
          },
          image: {
            image: item,
            type: "remote" as const,
          },
        })),
      ...params.addDescriptionMedia.map((image) => ({
        height: image.height,
        width: image.width,
        mediaType: "image" as const,
        onDelete: () => {
          setParams({
            addDescriptionMedia: params.addDescriptionMedia.filter(
              (i) => i.uri !== image.uri
            ),
          });
        },
        image: {
          uri: image.uri,
          type: "local" as const,
        },
      })),
    ],
    [
      params.addDescriptionMedia,
      params.removeDescriptionMediaIds,
      routine.descriptionImages,
      setParams,
    ]
  );

  const description = useMemo(() => {
    return params.description ?? routine.description ?? "";
  }, [routine?.description, params.description]);

  const title = useMemo(() => {
    return params.title ?? routine.title ?? "";
  }, [routine.title, params.title]);

  const { prepareForImageUpload, uploadImage } = useUploadImage();
  const [generateUploadLink] = useUploadImageLink();

  return (
    <Card noSpace defaultBorder>
      <ContentTitleInput
        autoFocus={props.route.params.mode === "CREATE"}
        title={title}
        setTitle={(title) =>
          setParams({
            title,
          })
        }
      />
      <AHSeparator />
      <RichDescriptionInputText
        description={description}
        setDescription={(newDescription) => {
          if (newDescription !== description) {
            setParams({ description: newDescription });
          }
        }}
        readOnly={false}
      />

      <RichDescriptionInputFiles
        onShowFile={({ id, name, url, type }) =>
          openFile({
            fileName: name,
            fileId: id,
            url,
            fileType: type,
          })
        }
        files={files}
        readOnly={false}
      />

      <RichDescriptionInputMedia media={media} />
      <RichDescriptionInputControllsWithPrepare
        addFile={() => {
          const orgWorkspaceId = props.route.params.orgWorkspaceId;
          if (orgWorkspaceId) {
            navigate("WorkspaceRoutineEditFileSelect", {
              orgWorkspaceId: orgWorkspaceId,
            });
          } else {
            navigate("RoutineEditFileSelect");
          }
        }}
        onPrepareUploadLink={generateUploadLink}
        onMediaAdd={async (media) => {
          const uploadId = prepareForImageUpload({
            width: media.width,
            height: media.height,
            localImageURI: media.uri,
          });

          setParams({
            addDescriptionMedia: [
              ...(params.addDescriptionMedia ?? []).filter(
                (i) => i.uri !== media.uri
              ),
              {
                height: media.height,
                width: media.width,
                uri: media.uri,
                id: media.imageId,
              },
            ],
          });
          uploadImage({
            uploadId: await uploadId,
            imageId: media.imageId,
            uploadURL: media.uploadURL,
          });
        }}
      />
    </Card>
  );
}
